'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import { LANGUAGES } from './const';
import { useAlternateLocaleSlug } from './useAlternateLocaleSlug';
import { Props } from './types';

/**
 * Locale switcher.
 *
 * The panel is rendered with the page and hidden until opened, rather than
 * mounted on click. It used to be the only route from German to English and it
 * did not exist until someone clicked it, so the entire English tree had no
 * crawlable inbound link (WB-813). Before any lookup resolves — which is the
 * state the server renders — each language points at its locale root, so the
 * served HTML always carries a real `/en/` and `/de/` href.
 */
const LanguageSwitcher = ({ currentLocale }: Props) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const pathname = usePathname();
  const panelId = useId();

  const { alternateSlug, apiType } = useAlternateLocaleSlug(
    currentLocale,
    pathname,
  );

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) setOpen(false);
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      buttonRef.current?.focus();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const currentLang = LANGUAGES.find((l) => l.code === currentLocale);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="true"
        onClick={() => setOpen(!open)}
        className="flex items-center space-x-1 text-sm font-medium text-brand-700"
      >
        <span>{currentLang?.code.toUpperCase()}</span>
        <ChevronDown
          className={clsx(
            'h-3 w-3 font-medium transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>

      <div
        id={panelId}
        hidden={!open}
        className={clsx(
          'absolute right-0 top-full z-50 mt-2 w-44 rounded-xl bg-white shadow-lg ring-1 ring-black/5 max-lg:bottom-full max-lg:top-auto max-lg:mb-2 lg:top-full lg:mt-2',
          open ? 'block' : 'hidden',
        )}
      >
        {LANGUAGES.map((lang) => {
          const isCurrent = lang.code === currentLocale;
          const collectionSegment =
            apiType && apiType !== 'pages' && alternateSlug
              ? `/${apiType}`
              : '';
          const href = isCurrent
            ? (pathname ?? `/${lang.code}/`)
            : `/${lang.code}${collectionSegment}/${alternateSlug}`;

          return (
            <Link
              key={lang.code}
              href={href}
              hrefLang={lang.code}
              aria-current={isCurrent ? 'true' : undefined}
              onClick={() => setOpen(false)}
              className={clsx(
                'block w-full rounded-lg px-3 py-3 text-left text-sm font-medium text-neutral-800 hover:bg-gray-100',
                isCurrent && 'bg-brand-100',
              )}
            >
              {lang.label}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default LanguageSwitcher;
